import moment from 'moment'
import { rem } from 'polished'
import type { ComponentProps, MouseEventHandler } from 'react'
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'

import type DropdownSheet from 'LuxKit/Dropdown/Sheet/DropdownSheet'
import DropdownSheetBase from 'LuxKit/Dropdown/Sheet/DropdownSheetBase'
import DropdownSheetBody from 'LuxKit/Dropdown/Sheet/DropdownSheetBody'
import DropdownSheetFooter from 'LuxKit/Dropdown/Sheet/DropdownSheetFooter'
import DropdownSheetHeader from 'LuxKit/Dropdown/Sheet/DropdownSheetHeader'
import LineArrowRightIcon from 'LuxKit/Icons/line/LineArrowRightIcon'
import LineExclamationTriangleIcon from 'LuxKit/Icons/line/LineExclamationTriangleIcon'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import BodyText from 'LuxKit/Typography/BodyText'
import type {
  AccommodationDayPickerSize,
  AccommodationMobileDayCellSelectHandler,
} from 'components/AccommodationKit/AccommodationCalendar/AccommodationCalendar.types'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import CalendarAvailabilityLegend from 'components/OfferPage/Filters/CalendarAvailabilityLegend'
import CalendarPromotionLegend from 'components/OfferPage/Filters/CalendarPromotionLegend'
import FormatDateTime from 'components/utils/Formatters/FormatDateTime'
import Group from 'components/utils/Group'
import Sheet from 'components/utils/Sheet'
import { CALENDAR_GA_EVENT_ACTION, CALENDAR_GA_EVENT_LABEL } from 'constants/analytics'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import UserAnalyticsActionContext from 'contexts/userAnalyticsActionContext'
import {
  buildAvailabilityVariantMap,
  getAlternativeDateOccupancyRooms,
  getAlternativeDatesDuration,
} from 'hooks/OfferPage/alternativeDatesUtils'
import { useAlternativeDateSelection } from 'hooks/OfferPage/useAlternativeDateSelection'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { useScreenSizeOnly } from 'hooks/useScreenSize'
import type { FeaturedDateRange } from 'lib/datetime/calendarDayViews'
import OfferCalendarLuxPlusToggle from 'luxPlus/components/OfferCalendarLuxPlusToggle'
import { hasCalendarDayLuxPlusPrices } from 'selectors/offerPage/propertyOfferPageSelectors'
import { useAssistantSurfaceDropdownBoundary } from 'tripPlanner/assistant/layout/AssistantSurfaceLayoutContext'

import OfferDatePicker from './OfferDatePicker'
import OfferDatePickerSelectBox from './OfferDatePickerSelectBox'

const DatePickerContainer = styled(Sheet)`
  width: min(${rem(800)}, calc(100dvw - ${rem(32)}));
  max-height: ${rem(492)};

  &.size-small {
    width: min(${rem(480)}, calc(100dvw - ${rem(32)}));
    max-height: min(${rem(360)}, calc(100dvh - ${rem(200)}));
  }
`

/** Compensates for DropdownSheetHeader's internal margin-top on the extension slot. Update if DropdownSheetHeader spacing changes. */
const LegendExtensionWrapper = styled.div`
  display: flex;
  align-items: center;
  margin-top: ${rem(-4)};
  margin-bottom: ${rem(4)};
`

interface Props extends Pick<ComponentProps<typeof DropdownSheet>, 'open' | 'boundaryRef'> {
  currentOfferId?: string
  rooms: Array<App.Occupants>
  propertyTimezone: string | undefined
  propertyPromotion: App.Property.PropertyPromotion | undefined
  hasDayLuxPlusPrices: boolean
  checkInDate?: string
  checkOutDate?: string
  isAlternativeDatesFlow: boolean
  availabilityData?: Record<string, boolean>
  availabilityLoading?: boolean
  duration?: number
  dayPickerSize?: AccommodationDayPickerSize
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void
}

function OfferDatePickerSelect(props: Props) {
  const {
    open,
    boundaryRef,
    currentOfferId,
    rooms,
    propertyTimezone,
    propertyPromotion,
    hasDayLuxPlusPrices,
    checkInDate,
    checkOutDate,
    isAlternativeDatesFlow,
    availabilityData,
    availabilityLoading,
    duration,
    dayPickerSize = 'large',
    onClick,
  } = props
  const intl = useIntl()
  const isMobile = useScreenSizeOnly('mobile')

  const [attemptedSubmit, setAttemptedSubmit] = useState(false)
  // store dates locally until we 'commit' them. This is done for performance reasons
  const [dates, setDates] = useState<{ checkIn?: string, checkOut?: string }>({
    checkIn: checkInDate,
    checkOut: checkOutDate,
  })

  useEffect(() => {
    if (isAlternativeDatesFlow) return
    setDates({ checkIn: checkInDate, checkOut: checkOutDate })
  }, [checkInDate, checkOutDate, isAlternativeDatesFlow])

  const dispatch = useOfferPageDispatch()
  const onUserAnalyticsAction = useContext(UserAnalyticsActionContext)

  const onCloseCross = useCallback(() => {
    onUserAnalyticsAction?.(CALENDAR_GA_EVENT_ACTION.CloseFilter, CALENDAR_GA_EVENT_LABEL.CloseCross)
    dispatch({ type: OfferPageActions.closeFilter })
    if (isAlternativeDatesFlow) {
      dispatch({ type: OfferPageActions.setAlternativeDatesFlow, isAlternativeDatesFlow: false })
    }
    if (!(dates.checkIn && !dates.checkOut)) {
      // only update state if it's not an 'invalid' (has check in, but not check out) selection
      dispatch({ type: OfferPageActions.setDates, checkIn: dates.checkIn, checkOut: dates.checkOut })
    }
  }, [dates.checkIn, dates.checkOut, dispatch, onUserAnalyticsAction, isAlternativeDatesFlow])

  const isInvalid = !!dates.checkIn && !dates.checkOut

  const onApply = useCallback<MouseEventHandler<HTMLButtonElement>>(() => {
    setAttemptedSubmit(true)
    if (!isInvalid) {
      onUserAnalyticsAction?.(CALENDAR_GA_EVENT_ACTION.Apply)
      dispatch({ type: OfferPageActions.setDates, checkIn: dates.checkIn, checkOut: dates.checkOut })
      dispatch({ type: OfferPageActions.closeFilter })
      if (isAlternativeDatesFlow) {
        dispatch({ type: OfferPageActions.setAlternativeDatesFlow, isAlternativeDatesFlow: false })
      }
    }
  }, [dates.checkIn, dates.checkOut, dispatch, isInvalid, onUserAnalyticsAction, isAlternativeDatesFlow])

  const onReset = useCallback(() => {
    setDates({ checkIn: undefined, checkOut: undefined })
  }, [])

  const triggerRef = useRef<HTMLButtonElement>(null)
  const bodyRef = useRef<HTMLDivElement>(null)
  const dropdownBoundary = useAssistantSurfaceDropdownBoundary(boundaryRef)

  useEffect(() => {
    if (!open) {
      // Each time we close dropdown, reset that the user attempted to submit it
      // we don't want to show the error message as soon as they open it again
      setAttemptedSubmit(false)
    }
  }, [open])

  const featuredDateRanges = useMemo(() => {
    if (isAlternativeDatesFlow || !propertyPromotion) return
    const type = propertyPromotion.type === 'campaign' ? 'exclusive' : 'special'
    return [{
      type,
      startIsoDate: propertyPromotion.travelFromIsoDate,
      endIsoDate: propertyPromotion.travelToIsoDate,
    }] as Utils.NonEmptyArray<FeaturedDateRange>
  }, [isAlternativeDatesFlow, propertyPromotion])

  const occupancyRooms = useMemo(() => getAlternativeDateOccupancyRooms(rooms), [rooms])

  const onAltDateRangeChange = useCallback((startDate: string, endDate: string | undefined) => {
    setDates({ checkIn: startDate, checkOut: endDate })
  }, [])

  const alternativeDatesDuration = useMemo(
    () => getAlternativeDatesDuration(dates.checkIn, dates.checkOut, duration),
    [dates.checkIn, dates.checkOut, duration],
  )

  const onAlternativeDayClick = useAlternativeDateSelection({
    startDate: dates.checkIn,
    endDate: dates.checkOut,
    offerId: currentOfferId,
    rooms: occupancyRooms,
    onDateRangeChange: onAltDateRangeChange,
  })

  const onDatesChange = useCallback<AccommodationMobileDayCellSelectHandler>(day => {
    if (isAlternativeDatesFlow) {
      onAlternativeDayClick(day)
      return
    }
    if (dates.checkIn && !dates.checkOut && moment(day.date).isAfter(dates.checkIn)) {
      setDates({ checkIn: dates.checkIn, checkOut: day.date })
    } else {
      setDates({ checkIn: day.date, checkOut: undefined })
    }
  }, [dates.checkIn, dates.checkOut, isAlternativeDatesFlow, onAlternativeDayClick])

  const availabilityVariantMap = useMemo(() => {
    if (!isAlternativeDatesFlow || !availabilityData) return undefined
    return buildAvailabilityVariantMap(availabilityData)
  }, [isAlternativeDatesFlow, availabilityData])

  const showAvailabilityLoading = isAlternativeDatesFlow && !!availabilityLoading

  const selectedMonth = useMemo(() => {
    if (isAlternativeDatesFlow && checkInDate) return checkInDate
    return undefined
  }, [isAlternativeDatesFlow, checkInDate])

  const resetDatesLabel = intl.formatMessage({
    id: 'DFYMkOzUKbON5-ZGj524',
    defaultMessage: 'Reset dates',
    description: 'Button text to reset selected dates in the date selection or calendar picker',
  })

  const modalTitle = intl.formatMessage({
    id: 'zrAFVE2yIHWizRnPU6qq',
    defaultMessage: 'Select dates',
    description: 'Title for date picker dropdown in the date selection or calendar picker',
  })

  return <div>
    <OfferDatePickerSelectBox
      ref={triggerRef}
      controlled={open}
      checkIn={dates.checkIn}
      checkOut={dates.checkOut}
      onClick={onClick}
    />
    <DropdownSheetBase
      size="content"
      anchorRef={triggerRef}
      triggerRef={triggerRef}
      autoHeight={dropdownBoundary.autoHeight}
      boundaryRef={dropdownBoundary.boundaryRef}
      onClose={onCloseCross}
      open={open && !(isMobile && onClick)}
      data-testid="offer-checkin-select"
      portaled={dropdownBoundary.portaled}
    >
      <DropdownSheetHeader
        title={modalTitle}
        size="M"
        alignment="start"
        onDismissClick={onCloseCross}
        actionsSlot={
          hasDayLuxPlusPrices ? <OfferCalendarLuxPlusToggle /> : undefined
        }
        extensionSlot={
          (isAlternativeDatesFlow || propertyPromotion) ? <LegendExtensionWrapper>
            {isAlternativeDatesFlow && <CalendarAvailabilityLegend duration={alternativeDatesDuration} />}
            {!isAlternativeDatesFlow && propertyPromotion && <CalendarPromotionLegend nights={propertyPromotion.minimumLos} />}
          </LegendExtensionWrapper> : undefined
        }
      />
      <DropdownSheetBody ref={bodyRef}>
        <DatePickerContainer
          className={`size-${dayPickerSize}`}
          padding={dayPickerSize === 'small' ? '16 0' : '24 0'}
        >
          <LoadingIndicator floating visible={showAvailabilityLoading} />
          <OfferDatePicker
            scrollerRef={bodyRef}
            size={dayPickerSize}
            propertyTimezone={propertyTimezone}
            featuredDateRanges={featuredDateRanges}
            checkInDate={dates.checkIn}
            checkOutDate={dates.checkOut}
            onDatesChange={onDatesChange}
            availabilityVariantMap={availabilityVariantMap}
            selectedMonth={selectedMonth}
            renderFromStart={isAlternativeDatesFlow}
          />
        </DatePickerContainer>
      </DropdownSheetBody>
      <DropdownSheetFooter
        startSlot={<>
          {isInvalid && attemptedSubmit && <BodyTextBlock
            variant="medium"
            colour="urgency-dark"
            startIcon={<LineExclamationTriangleIcon />}
          >
            {intl.formatMessage({
              id: 'THrMWAdkDv_FlwjgqqYS',
              defaultMessage: 'Select a checkout date',
              description: 'Error notification displayed when checkout date is not selected',
            })}
          </BodyTextBlock>}
          {dates.checkIn && dates.checkOut && <BodyText variant="medium">
            <Group direction="horizontal" verticalAlign="center">
              <span><FormatDateTime value={dates.checkIn} format="short-day-name-day-month" /></span>
              <LineArrowRightIcon size="XS" />
              <span><FormatDateTime value={dates.checkOut} format="short-day-name-day-month" /></span>
            </Group>
          </BodyText>}
        </>}
        secondaryActionProps={dates.checkIn ? { children: resetDatesLabel, onClick: onReset, kind: 'tertiary', size: 'large' } : undefined}
        primaryActionProps={{
          'data-testid': 'OfferDatePickerSelect-apply',
          size: 'large',
          children: intl.formatMessage({
            id: 'oUhIUmilE37uAT6cmYqQ',
            defaultMessage: 'Apply',
            description: 'Button text to apply selected dates in the date selection or calendar picker',
          }),
          onClick: onApply,
        }}
      />
    </DropdownSheetBase>
  </div>
}

type MappedProps = Pick<Props,
  | 'open'
  | 'currentOfferId'
  | 'rooms'
  | 'propertyTimezone'
  | 'propertyPromotion'
  | 'hasDayLuxPlusPrices'
  | 'checkInDate'
  | 'checkOutDate'
  | 'isAlternativeDatesFlow'
  | 'availabilityData'
  | 'availabilityLoading'
  | 'duration'
>

export default withOfferPageAndReduxState((state, appState): MappedProps => {
  const offerId = state.paginator?.currentOfferId ?? state.offer?.id
  const availabilityEntry = offerId ? appState.calendar.availabilityDateRange[offerId] : undefined
  return {
    open: state.activeFilter === 'checkin',
    currentOfferId: offerId,
    rooms: state.rooms,
    propertyTimezone: state.property?.location.timezone ?? state.offer?.property.timezone ?? state.bedbankOffer?.property.timezone,
    propertyPromotion: state.property?.promotion,
    hasDayLuxPlusPrices: hasCalendarDayLuxPlusPrices(state, appState),
    checkInDate: state.checkInDate,
    checkOutDate: state.checkOutDate,
    isAlternativeDatesFlow: !!state.isAlternativeDatesFlow,
    availabilityData: availabilityEntry?.data,
    availabilityLoading: availabilityEntry?.fetching,
    duration: state.duration,
  }
}, OfferDatePickerSelect)
